import React, {useState, useEffect, useContext, useRef} from "react";
import qs from 'query-string'

import * as Yup from "yup";
import {useHistory} from "react-router-dom";
import {Link as RouterLink} from "react-router-dom";
import {toast} from "react-toastify";
import {Formik, Form, Field} from "formik";

import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import {makeStyles, useTheme} from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import usePlans from '../../hooks/usePlans';
import {i18n} from "../../translate/i18n";
import {FormControl} from "@material-ui/core";
import {InputLabel, MenuItem, Select} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import {Helmet} from "react-helmet";

import {openApi} from "../../services/api";
import toastError from "../../errors/toastError";
import useSettings from "../../hooks/useSettings";
import ColorModeContext from "../../layout/themeContext";
import {getBackendUrl} from "../../config";
import clsx from "clsx";

const useStyles = makeStyles(theme => {
    const {mode} = theme;
    return ({
        root: {
            display: "flex",
            height: "100vh",
            width: "100vw",
            overflow: "hidden",
            backgroundColor: theme.palette.background.default,
        },
        leftContainer: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "#00B2FF",
                backgroundImage: "linear-gradient(326deg, rgba(0, 178, 255, 1) 0%, rgba(0, 70, 249, 1) 50%, rgba(0, 4, 25, 1) 100%)",
                zIndex: 1,
            },
            [theme.breakpoints.down("sm")]: {
                display: "none",
            },
        },
        ctaContent: {
            position: "relative",
            zIndex: 2,
            color: "#fff",
            textAlign: "center",
            width: "700px",
            height: "700px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "32px",
            borderRadius: "30px",
        },
        perex: {
            textAlign: "left",
            width: "100%",
            "& h2": {
                fontSize: "60px",
                fontWeight: 700,
                lineHeight: 1.1,
                margin: 0,
            },
            "& .mb-0": {
                marginBottom: "0 !important",
            },
            "& .with-make": {
                color: "#00ff80",
                marginBottom: "24px",
            },
            "& p": {
                fontSize: "20px",
                fontWeight: 300,
                lineHeight: 1.4,
                margin: "0 0 16px 0",
            },
            "& p:last-child": {
                fontSize: "14px",
                fontWeight: 400,
                opacity: 0.8,
                marginTop: "32px",
            }
        },
        rightContainer: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            maxWidth: "550px",
            height: "100%",
            backgroundColor: theme.palette.background.paper,
            overflowY: "auto",
            padding: "32px 70px",
            boxShadow: "-5px 0 15px rgba(0,0,0,0.1)",
            zIndex: 2,
            [theme.breakpoints.down("sm")]: {
                maxWidth: "100%",
            },
        },
        paper: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            animation: "$slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        form: {
            width: "100%",
            marginTop: theme.spacing(3),
        },
        submit: {
            margin: theme.spacing(3, 0, 2),
            background: mode === "light"
                ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: "white",
            borderRadius: "12px",
            padding: "12px 0",
            fontSize: "16px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
            border: "none",
            transition: "all 0.3s ease",
            "&:hover": {
                background: mode === "light"
                    ? `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.dark})`
                    : `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.dark})`,
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${theme.palette.primary.main}66`,
            },
            "&:active": {
                transform: "translateY(0)",
            },
        },
        textField: {
            "& .MuiInputBase-input": {
                color: mode === "light" ? "#374151" : "#FAFAFA",
                padding: "14.5px 10px",
            },
            "& .MuiInputBase-input::placeholder": {
                fontSize: "10px",
                color: mode === "light" ? "#9ca3af" : "#9ca3af",
                opacity: 1,
            },
            "& .MuiInputLabel-root": {
                fontWeight: 400,
                fontSize: "12px",
            },
        },
        inputContainer: {
            borderRadius: "6px !important",
        },
        logoImg: {
            width: "100%",
            maxWidth: "240px",
            height: "auto",
            maxHeight: "80px",
            margin: "0 auto 24px auto",
            objectFit: "contain",
        },
        "@keyframes slideInUp": {
            from: {
                opacity: 0,
                transform: "translateY(30px)",
            },
            to: {
                opacity: 1,
                transform: "translateY(0)",
            },
        },
    })
});

// Lista dos provedores de e-mail temporário mais comuns
const disposableEmailProviders = [
    "10minutemail.com", "mailinator.com", "yopmail.com", "tempmail.com",
    "guerrillamail.com", "sharklasers.com", "dispostable.com", "throwawaymail.com",
    "email-fake.com", "temp-mail.org", "temp-mail.ru", "tempmailaddress.com",
    "trashmail.com", "trashmail.net", "getairmail.com", "mohmal.com",
    "maildrop.cc", "anonaddy.com"
];

const Step1Schema = Yup.object().shape({
    companyName: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
    email: Yup.string()
        .email("E-mail inválido")
        .required("Obrigatório")
        .test('is-not-disposable', 'Provedores de e-mail temporário não são permitidos.', value => {
            if (!value) return false;
            const domain = value.split('@')[1];
            if (!domain) return false;
            return !disposableEmailProviders.includes(domain.toLowerCase());
        }),
    password: Yup.string().min(5, "Too Short!").max(50, "Too Long!").required("Required"),
    passwordConfirmation: Yup.string().oneOf([Yup.ref('password'), null], 'As senhas não são iguais*').required("Required"),
});

const Step2Schema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
    document: Yup.string()
        .test('document-format', 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos', function (value) {
            if (!value) return false;
            const numbers = value.replace(/\D/g, '');
            return numbers.length === 11 || numbers.length === 14;
        })
        .required("CPF ou CNPJ é obrigatório"),
    phone: Yup.string().required("Required"),
    planId: Yup.number().required("Required")
});

const SignUp = () => {
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const {colorMode} = useContext(ColorModeContext);
    const {appLogoFavicon, appName, mode} = colorMode;
    const {getPlanList} = usePlans()
    const [plans, setPlans] = useState([])
    const [loading, setLoading] = useState(false);
    const [validatingCnpj, setValidatingCnpj] = useState(false);
    const [lastValidatedDocument, setLastValidatedDocument] = useState("");
    const {getPublicSetting} = useSettings();

    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");

    const [activeStep, setActiveStep] = useState(0);
    const currentSchema = activeStep === 0 ? Step1Schema : Step1Schema.concat(Step2Schema);

    let companyId = null
    const params = qs.parse(window.location.search)
    if (params.companyId !== undefined) {
        companyId = params.companyId
    }

    const initialState = {name: "", email: "", password: "", passwordConfirmation: "", phone: "", companyId, companyName: "", document: "", planId: ""};

    const [user] = useState(initialState);

    useEffect(() => {
        getPublicSetting("userCreation")
            .then((data) => {
                if (data === "disabled") {
                    toast.error(i18n.t("signup.toasts.disabled"));
                    history.push("/login");
                }
            })
            .catch((error) => {
                console.log("Error reading setting", error);
            });

        getPublicSetting("appLogoBackgroundLight", companyId)
            .then((bgLight) => {
                if (bgLight) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgLight;
                    console.log("Background light URL:", fullUrl);
                    setBackgroundLight(fullUrl);
                } else {
                    setBackgroundLight("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo clara:", err);
                setBackgroundLight("");
            });

        getPublicSetting("appLogoBackgroundDark", companyId)
            .then((bgDark) => {
                if (bgDark) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + bgDark;
                    console.log("Background dark URL:", fullUrl);
                    setBackgroundDark(fullUrl);
                } else {
                    setBackgroundDark("");
                }
            })
            .catch((err) => {
                console.error("Erro ao carregar imagem de fundo escura:", err);
                setBackgroundDark("");
            });
    }, [history, companyId]);

    useEffect(() => {
        setLoading(true);
        const fetchData = async () => {
            const planList = await getPlanList({listPublic: "false"});

            setPlans(planList);
            setLoading(false);
        }
        fetchData();
    }, []);

    const validateCpf = (cpf) => {
        const cleanCpf = cpf.replace(/\D/g, '');

        if (cleanCpf.length !== 11) return false;

        if (/^(\d)\1{10}$/.test(cleanCpf)) return false;

        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cleanCpf.charAt(i)) * (10 - i);
        }
        let remainder = (sum * 10) % 11;
        if (remainder === 10 || remainder === 11) remainder = 0;
        if (remainder !== parseInt(cleanCpf.charAt(9))) return false;

        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cleanCpf.charAt(i)) * (11 - i);
        }
        remainder = (sum * 10) % 11;
        if (remainder === 10 || remainder === 11) remainder = 0;
        if (remainder !== parseInt(cleanCpf.charAt(10))) return false;

        return true;
    };

    const formatCpf = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 11);

        if (limitedNumbers.length <= 3) return limitedNumbers;
        if (limitedNumbers.length <= 6) return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3)}`;
        if (limitedNumbers.length <= 9) return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6)}`;
        return `${limitedNumbers.slice(0, 3)}.${limitedNumbers.slice(3, 6)}.${limitedNumbers.slice(6, 9)}-${limitedNumbers.slice(9)}`;
    };

    const formatCnpj = (value) => {
        const numbers = value.replace(/\D/g, '');
        const limitedNumbers = numbers.slice(0, 14);

        if (limitedNumbers.length <= 2) return limitedNumbers;
        if (limitedNumbers.length <= 5) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2)}`;
        if (limitedNumbers.length <= 8) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5)}`;
        if (limitedNumbers.length <= 12) return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8)}`;
        return `${limitedNumbers.slice(0, 2)}.${limitedNumbers.slice(2, 5)}.${limitedNumbers.slice(5, 8)}/${limitedNumbers.slice(8, 12)}-${limitedNumbers.slice(12)}`;
    };

    const detectDocumentType = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) return 'cpf';
        return 'cnpj';
    };

    const formatDocument = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) {
            return formatCpf(value);
        } else {
            return formatCnpj(value);
        }
    };

    const validateDocument = async (document, setFieldValue) => {
        const cleanDocument = document.replace(/\D/g, '');

        if (lastValidatedDocument === cleanDocument) {
            return true;
        }

        const documentType = detectDocumentType(document);

        if (documentType === 'cpf') {
            if (validateCpf(document)) {
                setLastValidatedDocument(cleanDocument);
                toast.success("CPF válido!");
                return true;
            } else {
                setLastValidatedDocument(cleanDocument);
                toast.error("CPF inválido!");
                return false;
            }
        } else {
            if (cleanDocument.length !== 14) return false;

            setValidatingCnpj(true);
            try {
                const response = await openApi.post("/auth/validate-cnpj", {cnpj: cleanDocument});
                const data = response.data;
                setValidatingCnpj(false);

                if (data.valid && data.data.nome) {
                    if (data.data.tipo === 'cnpj') {
                        setFieldValue('companyName', data.data.nome);
                        toast.success("CNPJ válido! Nome da empresa preenchido automaticamente.");
                    } else {
                        toast.success("CPF válido!");
                    }
                    setLastValidatedDocument(cleanDocument);
                    return true;
                } else {
                    setLastValidatedDocument(cleanDocument);
                    toast.error("Documento inválido ou não encontrado na Receita Federal");
                    return false;
                }
            } catch (error) {
                setValidatingCnpj(false);
                console.error("Erro ao validar documento:", error);
                toast.error("Erro ao validar documento. Tente novamente.");
                setLastValidatedDocument(cleanDocument);
                return false;
            }
        }
    };

    const handleSignUp = async values => {
        const normalizedValues = {
            ...values,
            email: values.email.toLowerCase().trim()
        };
        // Remove a confirmação pois o backend não precisa recebê-la
        delete normalizedValues.passwordConfirmation;
        try {
            await openApi.post("/auth/signup", normalizedValues);
            toast.success(i18n.t("signup.toasts.success"));
            history.push({
                pathname: "/verify-email",
                search: `?email=${normalizedValues.email}`,
                state: { password: normalizedValues.password }
            });
        } catch (err) {
            toastError(err);
        }
    };

    let finalBackground;

    const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
    const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';

    if (mode === "light") {
        if (hasValidLightBg) {
            finalBackground = `url(${backgroundLight})`;
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#f5f5f5";
        }
    } else {
        if (hasValidDarkBg) {
            finalBackground = `url(${backgroundDark})`;
        } else {
            finalBackground = theme.palette.background ? theme.palette.background.default : "#303030";
        }
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{appName || "AtendeChat - Cadastro"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"}/>
            </Helmet>

            <div className={classes.root}>
                <div 
                    className={classes.leftContainer}
                    style={{
                        backgroundImage: typeof finalBackground === "string" && finalBackground.includes("url(") ? finalBackground : "none",
                        backgroundColor: typeof finalBackground === "string" && !finalBackground.includes("url(") ? finalBackground : undefined,
                    }}
                >
                    <div className={classes.ctaContent}>
                        <div className={classes.perex}>
                            <h2 className="mb-0">Escale suas vendas</h2>
                            <h2 className="with-make">#comCliqBR</h2>
                            <p>Da qualificação de leads ao disparo de campanhas, centralize e automatize todo o seu atendimento no WhatsApp em uma única plataforma visual.</p>
                            <p>Vagas limitadas para Beta Founders | Preço vitalício garantido</p>
                        </div>
                    </div>
                </div>

                <div className={classes.rightContainer}>
                    <CssBaseline/>
                    <div className={classes.paper}>
                        <img
                            src={mode === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()}
                            alt="logo"
                            className={classes.logoImg}
                        />
                        <Typography component="h1" variant="h5" style={{ fontWeight: 600, marginBottom: "1rem" }}>
                            Criar uma conta
                        </Typography>
                        <Formik
                            initialValues={user}
                            enableReinitialize={true}
                            validationSchema={currentSchema}
                            onSubmit={(values, actions) => {
                                if (activeStep === 0) {
                                    setActiveStep(1);
                                    actions.setTouched({});
                                    actions.setSubmitting(false);
                                } else {
                                    setTimeout(() => {
                                        handleSignUp(values);
                                        actions.setSubmitting(false);
                                    }, 400);
                                }
                            }}
                        >
                            {({touched, errors, isSubmitting, setFieldValue, values}) => (
                                <Form className={classes.form}>
                                    {activeStep === 0 && (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="companyName"
                                                    className={classes.textField}
                                                    label="Nome da Organização"
                                                    error={touched.companyName && Boolean(errors.companyName)}
                                                    helperText={touched.companyName && errors.companyName}
                                                    name="companyName"
                                                    autoComplete="companyName"
                                                    InputProps={{ className: classes.inputContainer }}
                                                    autoFocus
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="email"
                                                    className={classes.textField}
                                                    label="Email"
                                                    name="email"
                                                    error={touched.email && Boolean(errors.email)}
                                                    helperText={touched.email && errors.email}
                                                    autoComplete="email"
                                                    inputProps={{style: {textTransform: 'lowercase'}}}
                                                    InputProps={{ className: classes.inputContainer }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    name="password"
                                                    className={classes.textField}
                                                    error={touched.password && Boolean(errors.password)}
                                                    helperText={touched.password && errors.password}
                                                    label="Senha"
                                                    type="password"
                                                    id="password"
                                                    autoComplete="current-password"
                                                    InputProps={{ className: classes.inputContainer }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    name="passwordConfirmation"
                                                    className={classes.textField}
                                                    error={(touched.passwordConfirmation || Boolean(values.passwordConfirmation)) && Boolean(errors.passwordConfirmation)}
                                                    helperText={(touched.passwordConfirmation || Boolean(values.passwordConfirmation)) ? errors.passwordConfirmation : ""}
                                                    label="Confirmar Senha"
                                                    type="password"
                                                    id="passwordConfirmation"
                                                    InputProps={{ className: classes.inputContainer }}
                                                />
                                            </Grid>
                                        </Grid>
                                    )}

                                    {activeStep === 1 && (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="document"
                                                    className={classes.textField}
                                                    label="CPF/CNPJ"
                                                    name="document"
                                                    error={touched.document && Boolean(errors.document)}
                                                    helperText={touched.document && errors.document || "Digite CPF (11 dígitos) ou CNPJ (14 dígitos)"}
                                                    autoComplete="document"
                                                    inputProps={{
                                                        maxLength: 18,
                                                        style: {textTransform: 'none'}
                                                    }}
                                                    InputProps={{
                                                        className: classes.inputContainer,
                                                        endAdornment: validatingCnpj && (
                                                            <CircularProgress size={20}/>
                                                        ),
                                                    }}
                                                    autoFocus
                                                    onChange={(e) => {
                                                        let value = e.target.value;
                                                        const numbers = value.replace(/\D/g, '');
                                                        if (lastValidatedDocument && lastValidatedDocument !== numbers) {
                                                            setLastValidatedDocument("");
                                                        }
                                                        const formattedValue = formatDocument(value);
                                                        setFieldValue('document', formattedValue);
                                                        if (numbers.length === 11 || numbers.length === 14) {
                                                            validateDocument(formattedValue, setFieldValue);
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    autoComplete="name"
                                                    name="name"
                                                    error={touched.name && Boolean(errors.name)}
                                                    helperText={touched.name && errors.name}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="name"
                                                    label="Nome de Usuário"
                                                    className={classes.textField}
                                                    InputProps={{ className: classes.inputContainer }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field
                                                    as={TextField}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="phone"
                                                    className={classes.textField}
                                                    label="Número de WhatsApp"
                                                    name="phone"
                                                    autoComplete="phone"
                                                    InputProps={{ className: classes.inputContainer }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <InputLabel htmlFor="plan-selection">Plano</InputLabel>
                                                <Field
                                                    as={Select}
                                                    variant="outlined"
                                                    fullWidth
                                                    id="plan-selection"
                                                    label="Plano"
                                                    className={clsx(classes.textField, classes.inputContainer)}
                                                    name="planId"
                                                    required
                                                >
                                                    {plans.map((plan, key) => (
                                                        <MenuItem key={key} value={plan.id}>
                                                            {plan.name} - Atendentes: {plan.users} - WhatsApp: {plan.connections} - Filas: {plan.queues} - R$ {plan.amount}
                                                        </MenuItem>
                                                    ))}
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    )}

                                    {activeStep === 0 ? (
                                        <Button
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            className={classes.submit}
                                        >
                                            Continuar
                                        </Button>
                                    ) : (
                                        <Grid container spacing={2} style={{ marginTop: 16 }}>
                                            <Grid item xs={4}>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => setActiveStep(0)}
                                                    style={{ height: '100%', borderRadius: 12, fontWeight: 600 }}
                                                >
                                                    Voltar
                                                </Button>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Button
                                                    type="submit"
                                                    fullWidth
                                                    variant="contained"
                                                    color="primary"
                                                    className={classes.submit}
                                                    style={{ margin: 0 }}
                                                    disabled={validatingCnpj || isSubmitting}
                                                >
                                                    Registrar-se
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    )}

                                    <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: 24, fontSize: '0.8rem' }}>
                                        Ao clicar no botão “Registrar-se”, você está criando uma conta {appName || "CliqBR"} e, com isso, concorda com os{" "}
                                        <Link href="https://cliqbr.com/home/privacy-policy/" target="_blank" rel="noopener noreferrer" color="primary">
                                            Termos de Uso
                                        </Link>
                                        {" "}e a{" "}
                                        <Link href="https://cliqbr.com/home/privacy-policy/" target="_blank" rel="noopener noreferrer" color="primary">
                                            Política de Privacidade
                                        </Link>
                                        {" "}da {appName || "CliqBR"}.
                                    </Typography>

                                    <Grid container justifyContent="center" style={{ marginTop: 16 }}>
                                        <Grid item>
                                            <Link
                                                href="#"
                                                variant="body2"
                                                component={RouterLink}
                                                to="/login"
                                                style={{ fontWeight: 600 }}
                                            >
                                                Já tem uma conta? Entrar!
                                            </Link>
                                        </Grid>
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default SignUp;
